import { create } from 'zustand';
import { useConnectionStore } from './connection';
import { useWalletEnvStore } from './wallet-env';
import { DEVICE_TYPE, MARKETPLACE_CHAINS } from '@shared/types/enums';
import { errorDict } from './ledger';
import { useApiServiceStore } from './api';
import { WalletDeviceApi } from '@shared/types/api';

interface Wallet {
  signTransaction(path: string, unsignedTx: any): Promise<WalletDeviceApi.Sign>;
  signMessage(message: string): Promise<WalletDeviceApi.Sign>;
}

interface WalletState {
  walletType: 'ledger' | 'trezor' | 'walletconnect' | 'polkadot-extension';
  wallet: Wallet | undefined;
  devicePath: string | undefined;
  setWalletType: (type: 'ledger' | 'trezor' | 'walletconnect' | 'polkadot-extension') => void;
  initializeWallet: () => Promise<void>;
  isLedger: () => boolean;
  signTransaction: (unsignedTx: any) => Promise<WalletDeviceApi.Sign>;
  signMessage: (message: string) => Promise<WalletDeviceApi.Sign>;
}

export const useWalletStore = create<WalletState>((set, get) => {
  class WalletConnectWallet implements Wallet {
    async signTransaction(path: string, unsignedTx: any): Promise<WalletDeviceApi.Sign> {
      const { isConnected, addressEWX, session, client } = useConnectionStore.getState();
      const { walletConst } = useWalletEnvStore.getState();

      if (!client || !addressEWX) {
        throw new Error('Client not initialized');
      }

      if (!isConnected || !session) {
        throw new Error('Session is not connected');
      }

      const result: WalletDeviceApi.Sign = await client.request({
        chainId: `polkadot:${walletConst.ewxChainId}`,
        topic: session.topic,
        request: {
          method: 'polkadot_signTransaction',
          params: {
            address: addressEWX,
            transactionPayload: unsignedTx,
          },
        },
      });

      return result;
    }

    async signMessage(message: string): Promise<WalletDeviceApi.Sign> {
      const { isConnected, addressEWX, session, client } = useConnectionStore.getState();
      const { walletConst } = useWalletEnvStore.getState();

      if (!client || !addressEWX) {
        throw new Error('Client not initialized');
      }

      if (!isConnected || !session) {
        throw new Error('Session is not connected');
      }

      // Request signature from the wallet
      const result = await client.request({
        chainId: `polkadot:${walletConst.ewxChainId}`,
        topic: session.topic,
        request: {
          method: 'polkadot_signMessage',
          params: {
            address: addressEWX,
            message,
          },
        },
      });

      return result as WalletDeviceApi.Sign;
    }
  }

  class LedgerWallet implements Wallet {
    async signMessage(message: string): Promise<WalletDeviceApi.Sign> {
      try {
        const workerApi = useApiServiceStore.getState().api;
        const { devicePath } = get();
        return await workerApi.deviceSignRaw(
          DEVICE_TYPE.Ledger,
          MARKETPLACE_CHAINS.EWX,
          message,
          devicePath,
        );
      } catch (error) {
        console.error('Ledger signMessage error:', error);
        if (error instanceof Error) {
          for (const [key, value] of Object.entries(errorDict)) {
            if (error.message.includes(key)) {
              throw new Error(value);
            }
          }
          throw error;
        }
        throw new Error('Error signing message');
      }
    }
    async signTransaction(path: string, unsignedTx: any): Promise<WalletDeviceApi.Sign> {
      try {
        const workerApi = useApiServiceStore.getState().api;
        return await workerApi.deviceSign(
          DEVICE_TYPE.Ledger,
          MARKETPLACE_CHAINS.EWX,
          JSON.stringify(unsignedTx),
          path,
        );
      } catch (error) {
        if (error instanceof Error) {
          for (const [key, value] of Object.entries(errorDict)) {
            if (error.message.includes(key)) {
              throw new Error(value);
            }
          }
        }

        throw new Error('Error signing transaction');
      }
    }
  }

  class PolkadotExtensionWallet implements Wallet {
    private async getPolkadotJsSigner() {
      const { addressEWX } = useConnectionStore.getState();

      // Directly access only the Polkadot.js extension
      const polkadotJs = window.injectedWeb3?.['polkadot-js'];

      if (!polkadotJs) {
        throw new Error('Polkadot.js extension not found');
      }

      // Enable the extension
      const injected = await polkadotJs.enable('EnergyWeb Marketplace');

      if (!injected.signer) {
        throw new Error('Signer not available');
      }

      return { signer: injected.signer, address: addressEWX };
    }

    async signTransaction(_path: string, unsignedTx: any): Promise<WalletDeviceApi.Sign> {
      const { isConnected, addressEWX } = useConnectionStore.getState();

      if (!addressEWX) {
        throw new Error('No address connected');
      }

      if (!isConnected) {
        throw new Error('Wallet is not connected');
      }

      try {
        const { signer } = await this.getPolkadotJsSigner();

        if (!signer.signPayload) {
          throw new Error('Signer does not support signPayload');
        }

        // Sign the transaction payload
        const { signature } = await signer.signPayload(unsignedTx);

        return { signature: signature as `0x${string}` };
      } catch (error) {
        console.error('Polkadot extension signTransaction error:', error);
        throw error;
      }
    }

    async signMessage(message: string): Promise<WalletDeviceApi.Sign> {
      const { isConnected, addressEWX } = useConnectionStore.getState();

      if (!addressEWX) {
        throw new Error('No address connected');
      }

      if (!isConnected) {
        throw new Error('Wallet is not connected');
      }

      try {
        const { signer, address } = await this.getPolkadotJsSigner();

        if (!signer.signRaw) {
          throw new Error('Signer does not support signRaw');
        }

        // Sign the raw message
        const { signature } = await signer.signRaw({
          address: address!,
          data: message,
          type: 'bytes',
        });

        return { signature: signature as `0x${string}` };
      } catch (error) {
        console.error('Polkadot extension signMessage error:', error);
        throw error;
      }
    }
  }

  return {
    walletType: 'walletconnect', // Default wallet type
    wallet: undefined,
    devicePath: undefined,
    isLedger: () => {
      const { walletType } = get();
      return walletType === 'ledger';
    },
    setWalletType: async (type: 'ledger' | 'trezor' | 'walletconnect' | 'polkadot-extension') => {
      set({ walletType: type });
      await get().initializeWallet();
    },
    initializeWallet: async () => {
      const { walletType } = get();
      let wallet: Wallet | undefined = undefined;
      if (walletType === 'ledger') {
        wallet = new LedgerWallet();
      } else if (walletType === 'walletconnect') {
        wallet = new WalletConnectWallet();
      } else if (walletType === 'polkadot-extension') {
        wallet = new PolkadotExtensionWallet();
      }
      else {
        throw new Error('Unsupported wallet type');
      }
      set({ wallet });
    },
    signTransaction: async (unsignedTx: any) => {
      const { wallet, devicePath } = get();
      if (!wallet) throw new Error('Wallet not initialized');
      const signature = await wallet.signTransaction(devicePath!, unsignedTx);
      return signature;
    },
    signMessage: async (message: string) => {
      const { wallet } = get();
      if (!wallet) throw new Error('Wallet not initialized');
      const signature = await wallet.signMessage(message);
      return signature;
    },
  };
});
